"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { AlertCircle, Check, Loader2, RefreshCw, X } from "lucide-react"
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"
import { useQuery } from "@tanstack/react-query"
import { subscriptionPaymentService } from "@/services/subscriptionPaymentService"
import { BillingCycleToggle } from "@/components/pricing/BillingCycleToggle"
import { useAuth } from "@/hooks/use-auth"

export function PricingSection() {
    const [billingCycle, setBillingCycle] = useState<number>(3) // Default to Quarterly (3 months)

    // `isPending`, not `isLoading`. React Query retries three times with a
    // backoff, and `isLoading` is `isPending && isFetching` — so it drops to
    // false during every gap between attempts. Gating on it let the "no plans"
    // copy flash in and out while the request was still very much in progress.
    const { data: plans, isPending, isError, isFetching, refetch } = useQuery({
        queryKey: ["subscription-plans"],
        queryFn: subscriptionPaymentService.getPlans,
    })

    const { user } = useAuth()
    const currentPlanName = user?.active_plan?.toLowerCase() ?? null

    const router = useRouter()
    const handlePurchase = (productKey: string) => {
        router.push(`/checkout?product_key=${productKey}&months=${billingCycle}`)
    }

    const displayPlans = plans ?? []

    return (
        <section className="pt-8 pb-8 md:pt-12 md:pb-12 bg-muted/30" id="pricing">
            <div className="container px-4 sm:px-6 md:px-8 mx-auto">
                <div className="text-center max-w-3xl mx-auto mb-16 space-y-6">
                    <div className="space-y-4">
                        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                            Choose your <span className="text-primary">Mastery</span> Plan
                        </h2>

                    </div>

                    <BillingCycleToggle value={billingCycle} onChange={setBillingCycle} />
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {/* Dynamic Plans */}
                    {isPending ? (
                        <div className="col-span-full flex justify-center items-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : (
                        displayPlans
                            .sort((a, b) => a.order_index - b.order_index)
                            .map((plan) => {
                                const baseBDT = plan.periods?.["1"]?.BDT ?? 0
                                const isFree = baseBDT === 0
                                const isBestValue = plan.is_best_value
                                const isHighlight = plan.is_popular

                                // Price for the selected billing cycle
                                const priceDisplay = plan.periods?.[String(billingCycle)]?.BDT ?? baseBDT * billingCycle
                                const originalPrice = baseBDT * billingCycle
                                const savings = Math.max(0, originalPrice - priceDisplay)
                                const perMonthPrice = billingCycle > 1 ? Math.round(priceDisplay / billingCycle) : baseBDT

                                return (
                                    <Card
                                        key={plan.product_key}
                                        className={`group relative flex flex-col h-full transition duration-500 rounded-2xl border border-white/10 dark:border-white/5 bg-background/50 dark:bg-black/40 backdrop-blur-xl overflow-hidden ${isHighlight
                                            ? "shadow-lg z-10"
                                            : isBestValue
                                                ? "shadow-lg"
                                                : "shadow-sm"
                                            }`}
                                    >
                                        {/* Animated Glow Hover */}
                                        <div className={`absolute -inset-0.5 rounded-2xl bg-gradient-to-b opacity-0 blur transition duration-500 group-hover:opacity-100 mix-blend-screen pointer-events-none ${isHighlight ? "from-primary/20 to-primary/0" :
                                            isBestValue ? "from-secondary/20 to-secondary/0" :
                                                "from-primary/10 to-primary/0"
                                            }`}></div>

                                        <div className="relative z-10 flex flex-col h-full bg-background/50 dark:bg-black/40">
                                            {isHighlight && (
                                                <div className="absolute top-0 left-0 right-0 w-full flex justify-center -translate-y-1/2">
                                                    <div className="bg-primary text-primary-foreground text-sm font-extrabold px-4 py-1 rounded-full shadow-md uppercase tracking-widest">
                                                        Most Popular
                                                    </div>
                                                </div>
                                            )}
                                            {isBestValue && (
                                                <div className="absolute top-0 left-0 right-0 w-full flex justify-center -translate-y-1/2">
                                                    <div className="bg-secondary text-secondary-foreground text-sm font-extrabold px-4 py-1 rounded-full shadow-md uppercase tracking-widest border border-secondary/20">
                                                        Best Value
                                                    </div>
                                                </div>
                                            )}

                                            <CardHeader className="pt-8 pb-2 text-center">
                                                < CardTitle className="text-3xl font-black tracking-tighter uppercase mb-1">
                                                    {plan.name}
                                                </CardTitle>
                                                {plan.description && (
                                                    <CardDescription className="font-medium text-foreground/80">
                                                        {plan.description}
                                                    </CardDescription>
                                                )}
                                            </CardHeader>

                                            <CardContent className="flex-1 space-y-4">
                                                <div className="flex flex-col items-center justify-center p-3 bg-muted/50 rounded-xl border border-border/50">
                                                    <>
                                                        <div className="flex items-baseline justify-center gap-1">
                                                            <span className="text-5xl font-extrabold tracking-tight text-foreground">
                                                                {isFree ? "Free" : `৳${perMonthPrice}`}
                                                            </span>
                                                            {!isFree && (
                                                                <span className="text-muted-foreground text-sm font-medium">/mo</span>
                                                            )}
                                                        </div>
                                                        {billingCycle > 1 && savings > 0 && (
                                                            <div className="mt-1 text-sm font-medium text-green-600 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded-full">
                                                                Save ৳{savings}
                                                            </div>
                                                        )}
                                                        {billingCycle > 1 && !isFree && (
                                                            <div className="mt-1 text-sm text-muted-foreground">
                                                                Total ৳{priceDisplay} /{billingCycle}mo
                                                            </div>
                                                        )}
                                                    </>
                                                </div>

                                                <ul className="space-y-3 text-left">
                                                    {(plan.features ?? []).map((feature, i) => (
                                                        <li
                                                            key={i}
                                                            className={`flex items-start gap-2 text-sm ${!feature.included ? "text-muted-foreground/60" : "text-foreground/90 font-medium"}`}
                                                        >
                                                            {feature.included ? (
                                                                <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                                                            ) : (
                                                                <X className="h-4 w-4 text-muted-foreground/30 shrink-0 mt-0.5" />
                                                            )}
                                                            <span className={!feature.included ? "line-through decoration-muted-foreground/30" : ""}>
                                                                {feature.name ?? feature.text}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </CardContent>

                                            <CardFooter className="pt-2 pb-5 relative z-10">
                                                {currentPlanName && currentPlanName === plan.name.toLowerCase() ? (
                                                    <Button
                                                        className="w-full font-semibold transition-all text-base shadow-sm bg-muted text-muted-foreground border border-border cursor-default"
                                                        size="lg"
                                                        disabled
                                                    >
                                                        Current Plan
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        className={`w-full font-semibold transition-all text-base shadow-lg ${plan.name.toLowerCase() === 'dedicated'
                                                            ? 'shadow-secondary/20 bg-secondary hover:bg-secondary/90 text-secondary-foreground'
                                                            : 'shadow-primary/20 bg-primary hover:bg-primary/90 text-primary-foreground'
                                                            }`}
                                                        size="lg"
                                                        onClick={() => handlePurchase(plan.product_key)}
                                                    >
                                                        {isFree ? "Start For Free" : `Get ${plan.name}`}
                                                    </Button>
                                                )}
                                            </CardFooter>
                                        </div>
                                    </Card>
                                )
                            })
                    )}

                    {/* A failed request and a genuinely empty catalogue used to render
                        the same "No plans available" line. On the pricing section of a
                        pricing-led page that turns a dropped connection into "this
                        product has nothing to sell you", with no way back. Separate the
                        two, and give the failure a retry. */}
                    {!isPending && isError && (
                        <div className="col-span-full flex flex-col items-center gap-4 py-12 text-center" role="alert">
                            <AlertCircle className="h-8 w-8 text-destructive" aria-hidden="true" />
                            <div className="space-y-1">
                                <p className="font-semibold text-foreground">We couldn&apos;t load the plans</p>
                                <p className="text-sm text-muted-foreground">
                                    Something went wrong on our side — your connection is probably fine.
                                </p>
                            </div>
                            <Button variant="outline" className="min-h-11" onClick={() => refetch()} disabled={isFetching}>
                                {isFetching ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                                        Retrying…
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                                        Try again
                                    </>
                                )}
                            </Button>
                        </div>
                    )}

                    {!isPending && !isError && displayPlans.length === 0 && (
                        <div className="col-span-full text-center py-12 text-muted-foreground">
                            No plans available at the moment.
                        </div>
                    )}
                </div>


            </div>
        </section>
    )
}
